import React, {useMemo} from 'react';
import PropTypes from 'prop-types';

import {useTranslation} from 'react-i18next';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import {CO2_INTERVENTION_TYPE, CO2_INTERVENTION_TYPE_OPTIONS, EXTRACTION_RATIO_OPTIONS} from '../../../../../config';
import {getLabelById} from '../../../../../utils/getLabelById';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';

//STYLES
const titleSx = {
  fontWeight: 900,
  color: 'secondary.main',
  fontSize: '14px',
  width: '20px',
  px: 1
};

const valueSx = {
  fontSize: '14px',
  width: '20px',
  px: 1
};

const tableContainer = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '30vw'
};

const PlanningInterventionTable = ({planningInterventions, onPlanningInterventionChange}) => {

  const {t} = useTranslation();

  const intermediateCuttingsSortedByAge = useMemo(() => planningInterventions && planningInterventions.intermediate_cuttings && planningInterventions.intermediate_cuttings
    .sort((a, b) => a.age - b.age), [planningInterventions]);

  const handleDeletePlanting = () => {
    // eslint-disable-next-line no-unused-vars
    //delete planningInterventions.planting;

    // eslint-disable-next-line no-unused-vars
    const {planting, ...updatedInterventions} = planningInterventions;

    onPlanningInterventionChange(updatedInterventions);
  };

  const handleDeleteFinalCutting = () => {
    // eslint-disable-next-line no-unused-vars
    const { final_cutting, ...newPerformance } = planningInterventions;
    onPlanningInterventionChange(newPerformance);
  };

  const handleDeleteIntermediateCuttings = (index) => {
    const intermediate_cuttings = planningInterventions.intermediate_cuttings.filter((item, i) => i !== index);
    onPlanningInterventionChange( { ...planningInterventions, intermediate_cuttings } );
  };

  return <>
    {planningInterventions.planting || planningInterventions.intermediate_cuttings || planningInterventions.final_cutting
      ? <TableContainer sx={tableContainer}>
        <Table size='small' sx={{mb: 2}}>
          <TableHead>
            <TableRow>
              <Tooltip title={t('Tipo de actuación')}>
                <TableCell align='left' sx={titleSx}>{t('Actuación').toUpperCase()}</TableCell>
              </Tooltip>
              <Tooltip title={t('Edad')}>
                <TableCell align='center' sx={titleSx}>{t('Edad')}</TableCell>
              </Tooltip>
              <Tooltip title={t('numberPerHa')}>
                <TableCell align='center' sx={titleSx}>{t('N')}</TableCell>
              </Tooltip>
              <Tooltip title={t('Relación de extracción de la clara')}>
                <TableCell align='center' sx={titleSx}>{t('Tipo')}</TableCell>
              </Tooltip>
              <Tooltip title='Proporción de árboles a extraer'>
                <TableCell align='center' sx={titleSx}>{t('%')}</TableCell>
              </Tooltip>
              <TableCell align='center' sx={titleSx}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              planningInterventions.planting &&
              <TableRow>
                <TableCell
                  sx={valueSx}>{getLabelById(CO2_INTERVENTION_TYPE.PLANTING, CO2_INTERVENTION_TYPE_OPTIONS)}</TableCell>
                <TableCell align='center' sx={valueSx}>0</TableCell>
                <TableCell align='center' sx={valueSx}>{planningInterventions.planting.number_per_ha || '-'}</TableCell>
                <TableCell align='center' sx={valueSx}>-</TableCell>
                <TableCell align='center' sx={valueSx}>-</TableCell>
                <TableCell align='center' sx={{...valueSx, cursor: 'pointer'}}><DeleteIcon sx={{color: 'grey'}} onClick={handleDeletePlanting}/></TableCell>
              </TableRow>
            }
            {
              planningInterventions && planningInterventions.intermediate_cuttings && intermediateCuttingsSortedByAge.map((intermediateCutting, index) =>
                <TableRow key={index}>
                  <TableCell
                    sx={valueSx}>{getLabelById(CO2_INTERVENTION_TYPE.INTERMEDIATE_CUTTINGS, CO2_INTERVENTION_TYPE_OPTIONS)}</TableCell>
                  <TableCell align='center' sx={valueSx}>{intermediateCutting.age}</TableCell>
                  <TableCell align='center' sx={valueSx}>-</TableCell>
                  <TableCell align='center'
                    sx={valueSx}>{getLabelById(intermediateCutting.extraction_ratio, EXTRACTION_RATIO_OPTIONS)}</TableCell>
                  <TableCell align='center' sx={valueSx}>{intermediateCutting.extraction_tree_proportion || '-'}</TableCell>
                  <TableCell align='center' sx={{...valueSx, cursor: 'pointer'}}><DeleteIcon sx={{color: 'grey'}}
                    onClick={() => handleDeleteIntermediateCuttings(index)}/></TableCell>
                </TableRow>
              )
            }
            {
              planningInterventions.final_cutting && <TableRow>
                <TableCell
                  sx={valueSx}>{getLabelById(CO2_INTERVENTION_TYPE.FINAL_CUTTING, CO2_INTERVENTION_TYPE_OPTIONS)}</TableCell>
                <TableCell align='center' sx={valueSx}>{planningInterventions.final_cutting.age}</TableCell>
                <TableCell align='center' sx={valueSx}>-</TableCell>
                <TableCell align='center' sx={valueSx}>-</TableCell>
                <TableCell align='center'
                  sx={valueSx}>{planningInterventions.final_cutting.extraction_tree_proportion || '-'}</TableCell>
                <TableCell align='center' sx={{...valueSx, cursor: 'pointer'}}><DeleteIcon sx={{color: 'grey'}}
                  onClick={handleDeleteFinalCutting}/></TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
      : <Typography color='error' sx={{mb: 1, pl: 0.5}}>{t('Sin Actuaciones')}</Typography>
    }
  </>;
};

PlanningInterventionTable.propTypes = {
  planningInterventions: PropTypes.object,
  onPlanningInterventionChange: PropTypes.func.isRequired,
};

export default PlanningInterventionTable;